import Vue from 'vue';

import AHttpService from '@d-services/a-http-service/a-http-service';
import { getErrorMessage } from '@d-shared/utils/errors';

function _getParamValue(param) {
  if (Array.isArray(param.value)) {
    return param.value
      .map(value => `${ param.query }=${ value }`)
      .join('&');
  }

  return `${ param.query }=${ param.value }`;
}

function _getQueryParams(search) {
  const searchTerm = _getSearchTerm(search);
  const searchParams = _getSearchParams(search);

  if (!searchTerm && !searchParams) {
    return '';
  }

  if (searchTerm && searchParams) {
    return `?${ searchTerm }&${ searchParams }`;
  }

  if (searchTerm) {
    return `?${ searchTerm }`;
  }

  return `?${ searchParams }`;
}

function _getSearchParams(search) {
  if (search.params.length === 0) {
    return null;
  }

  let params = [];
  const sortByParam = search.params.find((param) => param.key === 'sort_by');

  search.params.forEach((param) => {
    if (param.query) {
      params.push(`${ _getParamValue(param) }`);
      return;
    }

    if (param.order && param.key !== 'sort_by') {
      params.push(`sort_by=${ param.value }`);
      return;
    }

    if (param.key === 'date') {
      if (sortByParam && sortByParam.value === 'overall') return;
      const [ month, year ] = param.value.split('-');
      params.push(`year=${ year }`);
      if (!sortByParam) {
        params.push(`month=${ month }`);
      }
      return;
    }

    if (param.page) {
      params.push(`page=${ param.value }`);
    }
  });

  return params.join('&');
}

function _getSearchTerm(search) {
  let searchTerm = null;
  if (search.terms.length) {
    searchTerm = `query=${ search.terms.join(',') }`;
  }
  return searchTerm;
}

export default class ReviewerSearchService extends AHttpService {
  constructor(searchUrl) {
    super();

    this._searchUrl = searchUrl;

    this._reviewerSearch = {
      isSearching: false,
      meta: {
        total_count: 0,
      },
      results: [],
    };
    this._search = {
      params: [],
      terms: [],
    };
  }

  addSearchTag(tag) {
    if (tag.isTerm) {
      this.addSearchTerm(tag.value);
      return;
    }

    const existingParam = this._search.params.find(p => p.key === tag.key);
    if (existingParam) {
      existingParam.tags = tag.tags;
      existingParam.value = tag.value;
      return;
    }

    this._search.params.push(tag);
  }

  addSearchTerm(term) {
    if (this._search.terms.includes(term)) {
      return;
    }

    this._search.terms.push(term);
  }

  clearSearchTags() {
    const hiddenParams = [];
    this._search.params.forEach((param) => {
      if (param.tags && param.tags.length) {
        param.tags = param.tags.filter(t => t.hidden);
      }

      if (param.hidden || (param.tags && param.tags.length)) {
        hiddenParams.push(param);
      }
    });

    this._search = {
      params: hiddenParams,
      terms: [],
    };
  }

  async getReviewers(performSearch = false) {
    if (performSearch) {
      await this.search();
    }

    return this._reviewerSearch;
  }

  removeSearchTag(tag) {
    if (tag.isTerm) {
      this.removeSearchTerm(tag.value);
      return;
    }

    if (!tag.parentKey) {
      this._search.params = this._search.params.filter(p => p.key !== tag.key);
      return;
    }

    let parentTag = this._search.params.find(p => p.key === tag.parentKey);
    if (!parentTag) {
      return;
    }

    parentTag.tags = parentTag.tags.filter(p => p.key !== tag.key);
    if (parentTag.tags.length === 0) {
      this.removeSearchTag(parentTag);
      return;
    }

    parentTag.value = parentTag.tags.map(t => t.value);
  }

  removeSearchTerm(term) {
    this._search.terms = this._search.terms.filter(t => t !== term);
  }

  async search() {
    Vue.set(this._reviewerSearch, 'isSearching', true);

    try {
      const response = await this._http.get(`${ this._searchUrl }${ _getQueryParams(this._search) }`, null, true);
      Vue.set(this._reviewerSearch, 'results', response.data.reviewers);
      if (response.data.meta) Vue.set(this._reviewerSearch, 'meta', response.data.meta);
    } catch (error) {
      Vue.duiNotificationManager.error(getErrorMessage(error));
    }

    Vue.set(this._reviewerSearch, 'isSearching', false);
  }
}
